import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { SessionService } from 'src/app/auth/session/session.service';
import { StatusColor } from 'src/app/shared/status-indicator/status-indicator.component';
import { ReleaseViewConfigModalComponent } from '../release-view-config-modal/release-view-config-modal.component';
import {
  ChangeType,
  FirmwareChange,
  ModuleChange,
  PhaseChange,
  Release,
  ReleaseChangeDetails,
  ReleaseService,
  ReleaseStatusChange,
} from '../services/release.service';

@Component({
  selector: 'dcc-release-approval-modal',
  templateUrl: './release-approval-modal.component.html',
  styleUrls: ['./release-approval-modal.component.scss'],
})
export class ReleaseApprovalModalComponent implements OnInit {
  @Output() refreshPage: EventEmitter<any> = new EventEmitter();
  statusColor = StatusColor.yellow;
  statusString = 'Awaiting Approval';

  release: Release;

  approver: string;
  changes: ReleaseChangeDetails;
  currentApprovalOption = '';
  estimatedTotal = 0;
  firmwareAdded: FirmwareChange[] = [];
  firmwareRemoved: FirmwareChange[] = [];
  modulesAdded: ModuleChange[] = [];
  modulesEdited: ModuleChange[] = [];
  modulesRemoved: ModuleChange[] = [];
  phaseChanges: PhaseChange[] = [];
  requestCreator: string;
  description: string;
  timestamp: Date;
  releaseStatusChange: ReleaseStatusChange;
  isDelete = false;

  isLoading = false;
  constructor(
    public activeModal: NgbActiveModal,
    public releaseService: ReleaseService,
    private sessionService: SessionService,
    public toastr: ToastrService,
    public modalService: NgbModal,
  ) {}

  async ngOnInit() {
    this.isLoading = true;
    this.changes = await this.releaseService.getReleaseChanges(this.release.id);
    this.parseChanges();
    this.isLoading = false;
    this.estimatedTotal = await this.estimateImpactedDevices();
  }

  close() {
    this.refreshPage.emit();
    this.activeModal.close(false);
  }

  getStatusColor(status: string): StatusColor {
    switch (status) {
      case 'Deployed':
        return StatusColor.green;
      case 'Rejected':
        return StatusColor.red;
      case 'Disabled':
        return StatusColor.red;
      case 'Pending':
        return StatusColor.yellow;
      case 'InProgress':
        return StatusColor.blue;
      case 'PendingReview':
        return StatusColor.yellow;
      case 'Scheduled':
        return StatusColor.yellow;
      default:
        return StatusColor.yellow;
    }
  }

  displayConfig(config: string) {
    const modal = this.modalService.open(ReleaseViewConfigModalComponent, {
      size: 'lg',
      windowClass: 'lytxModal',
      centered: true,
    });
    modal.componentInstance.headerText = `JSON Configuration`;
    modal.componentInstance.bodyText = config;
  }

  hasProposedConfig(change: ModuleChange): boolean {
    if (
      change.configurationChange &&
      change.configurationChange.proposedConfiguration
    ) {
      return true;
    }
    return false;
  }

  hasCurrentConfig(change: ModuleChange): boolean {
    if (
      change.configurationChange &&
      change.configurationChange.currentConfiguration
    ) {
      return true;
    }
    return false;
  }

  getDeploymentDate(change: PhaseChange): Date {
    if (change.proposedDeploymentDate) {
      return change.proposedDeploymentDate;
    }
    return change.currentDeploymentDate;
  }

  deploymentDateChange(change: PhaseChange): boolean {
    if (
      change.proposedDeploymentDate &&
      change.currentDeploymentDate !== change.proposedDeploymentDate
    ) {
      return true;
    }
    return false;
  }

  isNotNewRelease(): boolean {
    return this.release.status !== 'Pending';
  }

  onApprovalOptionChange(event: any) {
    this.currentApprovalOption = event.target.value;
  }

  estimateImpactedDevices(): Promise<number> {
    return this.releaseService.simulateChanges(
      this.release.id,
      this.changes.timestamp,
    );
  }

  async onSave() {
    await this.releaseService.approveOrRejectRelease(
      this.release.id,
      this.getUserName(),
      this.currentApprovalOption,
    );
    this.refreshPage.emit();
    this.close();
  }

  private parseChanges() {
    this.modulesAdded = this.changes.moduleChanges.filter(
      (m) => m.changeType === ChangeType.Add,
    );
    this.modulesEdited = this.changes.moduleChanges.filter(
      (m) => m.changeType === ChangeType.Edit,
    );
    this.modulesRemoved = this.changes.moduleChanges.filter(
      (m) => m.changeType === ChangeType.Remove,
    );
    this.firmwareAdded = this.changes.firmwareChanges.filter(
      (f) => f.changeType === ChangeType.Add,
    );
    this.firmwareRemoved = this.changes.firmwareChanges.filter(
      (f) => f.changeType === ChangeType.Remove,
    );
    this.modulesAdded.concat(this.getFirmwareAddedAsModule());
    this.modulesRemoved.concat(this.getFirmwareRemovedAsModule());
    this.phaseChanges = this.changes.phaseChanges;
    this.requestCreator = this.changes.requestCreator;
    this.description = this.changes.description;
    this.timestamp = this.changes.timestamp;
    this.releaseStatusChange = this.changes.releaseStatusChange;
    this.isDelete = this.changes.isDelete;
  }

  private getFirmwareAddedAsModule(): ModuleChange[] {
    return this.firmwareAdded
      .filter((f) => f.changeType === ChangeType.Add)
      .map((f) => {
        return {
          changeType: ChangeType.Add,
          configurationChange: null,
          versionChange: {
            currentVersion: null,
            proposedVersion: f.firmware,
          },
          name: f.firmware,
        };
      });
  }

  private getFirmwareRemovedAsModule(): ModuleChange[] {
    return this.firmwareRemoved
      .filter((f) => f.changeType === ChangeType.Remove)
      .map((f) => {
        return {
          changeType: ChangeType.Remove,
          configurationChange: null,
          versionChange: {
            currentVersion: f.firmware,
            proposedVersion: null,
          },
          name: f.firmware,
        };
      });
  }

  private getUserName(): string {
    return this.sessionService.getCurrentSession().name;
  }
}
